import { useModal } from '@jutro/components';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { CoverablesService } from 'gw-capability-codegen';
import _ from 'lodash';
import React, { useCallback, useContext, useState } from 'react';
import { CodegenContext } from '../../components/CodegenContext';
import Coverable from '../../components/Coverable/Coverable';
import useAccordionStates from '../../hooks/useAccordionStates';
import messages from './CoverablesPage.messages';
import presentationMetadata from './CoverablesPage.metadata.json5';
import styles from './CoverablesStyles.module.scss';

const SCREENS = {
    coverablesView: 'COVERABLES_VIEW',
    addCoverableView: 'ADD_COVERABLE_VIEW',
    editCoverableView: 'EDIT_COVERABLE_VIEW',
};

function CoverablesPage(props) {
    const { showConfirm } = useModal();

    const { authHeader } = useAuthentication();
    const [isVMFormValid, setVMFormValid] = useState(false);
    const { initialValidation } = useValidation('CoverablesPage');
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { wizardData, updateWizardData, currentStep } = props;
    const { pageKey } = currentStep;
    const jobId =
        (wizardData.quoteID && wizardData.quoteID.value) ||
        wizardData.jobID.value;
    const sessionUUID = wizardData.sessionUUID.value;
    const { lobName } = useContext(CodegenContext);
    const metadata = presentationMetadata[pageKey];

    const [screen, updateScreen] = useState(SCREENS.coverablesView);
    const [coverableCallbackProps, updateCoverableCallbackProps] = useState({
        path: '',
    });

    const [coverableData, updateCoverableData] = useState({});
    const { openAccordions, accordionCallbackProps } = useAccordionStates({
        ownerVM: wizardData,
    });

    const onNext = useCallback(async () => {
        wizardData.value = await LoadSaveService.updateDraftSubmission(
            wizardData.value,
            authHeader
        );
        return wizardData;
    }, [LoadSaveService, authHeader, wizardData]);

    const onSaveCoverable = useCallback(
        (changed, newCoverable) => {
            if (changed) {
                const { path } = coverableCallbackProps;
                const coverables = _.get(wizardData, `${path}.value`);
                if (coverables) {
                    coverables.push(newCoverable);
                } else {
                    _.set(wizardData, path, [newCoverable]);
                }
                updateWizardData(wizardData);
                openAccordions(coverableCallbackProps);
            }
            updateScreen(SCREENS.coverablesView);
        },
        [wizardData, coverableCallbackProps, updateWizardData, openAccordions]
    );

    const onEditCoverable = useCallback(
        (changed, editedCoverable) => {
            if (changed) {
                const { path } = coverableCallbackProps;
                const pathWithIndex = `${path}.value`;
                _.set(wizardData, pathWithIndex, editedCoverable);
                updateWizardData(wizardData);
            }
            updateScreen(SCREENS.coverablesView);
        },
        [wizardData, coverableCallbackProps, updateWizardData]
    );

    const addCoverable = useCallback(
        callbackProps => {
            const parentPath = callbackProps.path.substring(
                0,
                callbackProps.path.lastIndexOf('.')
            );
            const owningCoverablePublicID = _.get(
                wizardData,
                `${parentPath}.publicID.value`
            );
            const allCallbackProps = {
                ...callbackProps,
                owningCoverablePublicID,
            };
            updateCoverableCallbackProps(allCallbackProps);
            updateScreen(SCREENS.addCoverableView);
        },
        [updateCoverableCallbackProps, updateScreen, wizardData]
    );

    const editCoverable = useCallback(
        callbackProps => {
            const { path } = callbackProps;
            const coverableToEdit = _.get(wizardData, `${path}.value`);
            updateCoverableData(coverableToEdit);
            updateCoverableCallbackProps(callbackProps);
            updateScreen(SCREENS.editCoverableView);
        },
        [wizardData, updateCoverableCallbackProps, updateScreen]
    );

    const deleteCoverable = useCallback(
        callbackProps => {
            const { path, iterableIndex, modalTitle, modalMessage } =
                callbackProps;

            showConfirm({
                title: modalTitle,
                message: modalMessage,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: messages.removeActionLabel,
            }).then(async results => {
                if (results === 'cancel' || results === 'close') {
                    return _.noop();
                }
                // everything up to children
                const coverablesPath = path.substring(
                    0,
                    path.lastIndexOf('.children')
                );
                const coverables = _.get(wizardData, `${coverablesPath}.value`);
                const coverableToDelete = coverables[iterableIndex];

                const params = [jobId, sessionUUID, coverableToDelete];
                CoverablesService.callMethod(
                    lobName,
                    callbackProps.coverableType,
                    'remove',
                    params,
                    authHeader
                ).then(() => {
                    coverables.splice(iterableIndex, 1);
                    updateWizardData(wizardData);
                });
            }, _.noop);
        },
        [
            showConfirm,
            wizardData,
            jobId,
            sessionUUID,
            lobName,
            authHeader,
            updateWizardData,
        ]
    );

    let toRender;
    if (screen === SCREENS.coverablesView) {
        const callbackMap = {
            addCoverable,
            editCoverable,
            deleteCoverable,
        };
        const overrideProps = {
            ...accordionCallbackProps(),
        };

        toRender = (
            <ViewModelForm
                uiProps={metadata.content}
                model={wizardData}
                onModelChange={updateWizardData}
                onValidationChange={setVMFormValid}
                callbackMap={callbackMap}
                overrideProps={overrideProps}
                classNameMap={styles}
            />
        );
    } else if (screen === SCREENS.addCoverableView) {
        const addCoverableMetadata =
            presentationMetadata[coverableCallbackProps.metadataKey];
        toRender = (
            <Coverable
                key="addCoverable"
                callbackProps={coverableCallbackProps}
                onSaveCoverable={onSaveCoverable}
                addCoverableMetadata={addCoverableMetadata}
                lobName={lobName}
                jobID={jobId}
                sessionUUID={sessionUUID}
            />
        );
    } else if (screen === SCREENS.editCoverableView) {
        const addCoverableMetadata =
            presentationMetadata[coverableCallbackProps.metadataKey];
        toRender = (
            <Coverable
                key="editCoverable"
                callbackProps={coverableCallbackProps}
                onSaveCoverable={onEditCoverable}
                coverableData={coverableData}
                addCoverableMetadata={addCoverableMetadata}
                lobName={lobName}
                sessionUUID={sessionUUID}
                jobID={jobId}
            />
        );
    }

    return (
        <WizardPage
            onNext={onNext}
            skipWhen={() => initialValidation}
            disableNext={!isVMFormValid}
            showCancel={screen === SCREENS.coverablesView}
            showPrevious={screen === SCREENS.coverablesView}
            showNext={screen === SCREENS.coverablesView}
        >
            {toRender}
        </WizardPage>
    );
}

CoverablesPage.propTypes = wizardProps;
export default CoverablesPage;
